import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { Coordinates, CoordinatesUnit, CoordinatesUnitEnum, NullableCoordinates } from '@/types/commons/commons.types';

type GPSCoordinatesFieldContextType = {
  position: Coordinates | null;
  lat: string | null;
  long: string | null;
  unit: CoordinatesUnit | null;
  setLat: (v: string | null) => void;
  setLong: (v: string | null) => void;
  setUnit: (v: CoordinatesUnit | null) => void;
  mouseSelectedPosition?: NullableCoordinates;
  isSelectionEnabled?: boolean;
  setSelectionOn?: (active: boolean) => void;
};

const GPSCoordinatesFieldContext = createContext<GPSCoordinatesFieldContextType | null>(null);

export const GPSCoordinatesFieldContextProvider: FC<{
  children: ReactNode;
  coordinates?: Coordinates;
  mouseSelectedPosition?: NullableCoordinates;
  isSelectionEnabled?: boolean;
  setSelectionOn?: (active: boolean) => void;
}> = ({ coordinates, children, mouseSelectedPosition, isSelectionEnabled, setSelectionOn }) => {
  const [lat, setLat] = useState<string | null>(coordinates?.latitude ? coordinates.latitude.toString() : null);
  const [long, setLong] = useState<string | null>(coordinates?.longitude ? coordinates.longitude.toString() : null);
  const [unit, setUnit] = useState<CoordinatesUnit | null>(CoordinatesUnitEnum.DECIMAL_DEGREES);

  const setPosition = (value: Coordinates) => {
    const { latitude, longitude } = value;
    setLat(latitude.toString());
    setLong(longitude.toString());
  };

  const position: Coordinates | null = useMemo(() => {
    if (lat == null || long == null) return null;
    const parsedLat = Number.parseFloat(lat);
    const parsedLong = Number.parseFloat(long);
    if (Number.isNaN(parsedLat) || Number.isNaN(parsedLong)) return null;
    return {
      latitude: parsedLat,
      longitude: parsedLong,
      type: '2D',
    };
  }, [lat, long]);

  useEffect(() => {
    if (coordinates?.latitude) setLat(coordinates.latitude.toString());
    if (coordinates?.longitude) setLong(coordinates.longitude.toString());
  }, [coordinates]);

  useEffect(() => {
    if (mouseSelectedPosition?.latitude && mouseSelectedPosition.longitude) {
      setLat(mouseSelectedPosition.latitude.toString());
      setLong(mouseSelectedPosition.longitude.toString());
      setPosition({
        latitude: mouseSelectedPosition.latitude,
        longitude: mouseSelectedPosition.longitude,
        type: mouseSelectedPosition.type,
      });
    }
  }, [mouseSelectedPosition]);

  const value = useMemo(
    () => ({
      position,
      mouseSelectedPosition,
      isSelectionEnabled,
      setSelectionOn,
      lat,
      long,
      unit,
      setLat,
      setLong,
      setUnit,
    }),
    [lat, long, position, unit, mouseSelectedPosition, isSelectionEnabled, setSelectionOn],
  );

  return <GPSCoordinatesFieldContext value={value}>{children}</GPSCoordinatesFieldContext>;
};

export const useGPSCoordinatesFieldContext = () => {
  const context = useContext(GPSCoordinatesFieldContext);
  if (!context) {
    throw new Error('useGPSCoordinatesFieldContext must be used within a GPSCoordinatesFieldProvider');
  }
  return context;
};
